import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CycleEndBanner } from '../components/CycleEndBanner';
import { BidInvitations } from '../components/BidInvitations';

// ─── Shared types ──────────────────────────────────────────────────────
type YardSize = 'small' | 'medium' | 'large' | 'xl';
const YARD_LABEL: Record<YardSize, string> = {
  small:  'Small  ·  < 1/8 acre',
  medium: 'Medium ·  1/8 – 1/4 acre',
  large:  'Large  ·  1/4 – 1/2 acre',
  xl:     'XL     ·  1/2 acre +',
};
const WEEKDAYS = ['sun','mon','tue','wed','thu','fri','sat'] as const;
const WEEKDAY_LABELS: Record<typeof WEEKDAYS[number], string> = {
  sun: 'Sun', mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat',
};
const WINDOWS = ['morning','afternoon','evening'] as const;

interface Availability {
  weekdays: string[];
  windows: string[];
  blackout_dates: string[];
  notes: string;
}
function parseAvail(raw: string | null | undefined): Availability {
  if (!raw) return { weekdays: [], windows: [], blackout_dates: [], notes: '' };
  try {
    const p = JSON.parse(raw) as Partial<Availability>;
    return {
      weekdays: Array.isArray(p.weekdays) ? p.weekdays : [],
      windows: Array.isArray(p.windows) ? p.windows : [],
      blackout_dates: Array.isArray(p.blackout_dates) ? p.blackout_dates : [],
      notes: typeof p.notes === 'string' ? p.notes : '',
    };
  } catch {
    return { weekdays: [], windows: [], blackout_dates: [], notes: '' };
  }
}

interface ResidentRow {
  id: number; created_at: string; status: string; archived_at: string | null;
  first_name: string; last_name: string;
  address: string; city: string; zip: string; county: string;
  cycle_start: string | null; cycle_end: string | null; cycle_number: number;
  assigned_track: 'mowkin' | 'volunteer' | null;
  score: number; tier: string;
  yard_size: YardSize | null;
  availability_json: string | null;
  contact_pref: string | null;
  access_notes: string | null;
  gate_code: string | null;
}
interface VolunteerRow {
  id: number; created_at: string; status: string; archived_at: string | null;
  first_name: string; last_name: string;
  city: string | null; county: string | null;
  vetted_at: string | null;
  max_yard_size: YardSize | null;
  availability_json: string | null;
  service_radius_miles: number | null;
  can_haul: string | null;
  preferred_county: string | null;
}
interface ProviderRow {
  id: number; created_at: string; status: string; archived_at: string | null;
  business_name: string; owner_name: string; capacity_per_month: string;
  max_yard_size: YardSize | null;
  availability_json: string | null;
  service_radius_miles: number | null;
  preferred_county: string | null;
}
interface HireRow {
  id: number; created_at: string; status: string; archived_at: string | null;
  contact_name: string; service_address: string; city: string; zip: string;
  service_type: string;
}
interface AssignmentRow {
  id: number;
  scheduled_date: string;
  time_window: string | null;
  status: string;
  admin_notes: string | null;
  mowkin_notes: string | null;
  completed_at: string | null;
  resident_id: number;
  volunteer_id: number | null;
  provider_id: number | null;
  resident_name: string | null;
  resident_address: string | null;
  resident_city: string | null;
  resident_yard_size: string | null;
  mowkin_label: string | null;
  mowkin_kind: 'volunteer' | 'provider' | null;
}
interface ApiData {
  user: { id: number; email: string; name: string | null; role: string };
  residents: ResidentRow[];
  providers: ProviderRow[];
  volunteers: VolunteerRow[];
  hires: HireRow[];
  assignments: AssignmentRow[];
}

// ─── Tiny presentational helpers ───────────────────────────────────────
function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString();
}
function StatusPill({ status }: { status: string }) {
  const map: Record<string, string> = {
    new: 'bg-forest/10 text-forest',
    pending: 'bg-terracotta text-cream',
    approved: 'bg-forest text-cream',
    denied: 'bg-terracotta/20 text-terracotta',
    vetted: 'bg-forest text-cream',
    rejected: 'bg-terracotta/20 text-terracotta',
    inactive: 'bg-forest/10 text-forest/60',
    proposed: 'bg-sand text-forest',
    accepted: 'bg-forest text-cream',
    declined: 'bg-terracotta/20 text-terracotta',
    completed: 'bg-forest text-cream',
    cancelled: 'bg-forest/10 text-forest/60',
    no_show: 'bg-terracotta/20 text-terracotta',
  };
  const cls = map[status] || 'bg-forest/10 text-forest';
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] tracking-[0.15em] uppercase ${cls}`}>
      {status.replace('_', ' ')}
    </span>
  );
}

// ─── Availability editor ───────────────────────────────────────────────
function AvailabilityEditor({
  value,
  onChange,
}: {
  value: Availability;
  onChange: (v: Availability) => void;
}) {
  const toggleDay = (d: string) => {
    const next = value.weekdays.includes(d)
      ? value.weekdays.filter((x) => x !== d)
      : [...value.weekdays, d];
    onChange({ ...value, weekdays: next });
  };
  const toggleWindow = (w: string) => {
    const next = value.windows.includes(w)
      ? value.windows.filter((x) => x !== w)
      : [...value.windows, w];
    onChange({ ...value, windows: next });
  };
  const [newBlackout, setNewBlackout] = useState('');
  const addBlackout = () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(newBlackout)) return;
    if (value.blackout_dates.includes(newBlackout)) return;
    onChange({ ...value, blackout_dates: [...value.blackout_dates, newBlackout].sort() });
    setNewBlackout('');
  };
  const removeBlackout = (d: string) => {
    onChange({ ...value, blackout_dates: value.blackout_dates.filter((x) => x !== d) });
  };

  return (
    <div className="space-y-5">
      <div>
        <div className="text-xs tracking-[0.2em] uppercase text-forest/60 mb-2">Days I'm available</div>
        <div className="flex flex-wrap gap-2">
          {WEEKDAYS.map((d) => {
            const active = value.weekdays.includes(d);
            return (
              <button
                key={d}
                type="button"
                onClick={() => toggleDay(d)}
                className={`px-3 py-1.5 text-sm border transition-colors ${
                  active
                    ? 'bg-forest text-cream border-forest'
                    : 'bg-cream text-forest border-forest/20 hover:border-terracotta'
                }`}
              >
                {WEEKDAY_LABELS[d]}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <div className="text-xs tracking-[0.2em] uppercase text-forest/60 mb-2">Time of day</div>
        <div className="flex flex-wrap gap-2">
          {WINDOWS.map((w) => {
            const active = value.windows.includes(w);
            return (
              <button
                key={w}
                type="button"
                onClick={() => toggleWindow(w)}
                className={`px-3 py-1.5 text-sm border capitalize transition-colors ${
                  active
                    ? 'bg-forest text-cream border-forest'
                    : 'bg-cream text-forest border-forest/20 hover:border-terracotta'
                }`}
              >
                {w}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <div className="text-xs tracking-[0.2em] uppercase text-forest/60 mb-2">Blackout dates (I'm out)</div>
        <div className="flex items-center gap-2 mb-2">
          <input
            type="date"
            value={newBlackout}
            onChange={(e) => setNewBlackout(e.target.value)}
            className="border border-forest/20 px-2 py-1 text-sm bg-cream focus:outline-none focus:border-terracotta"
          />
          <button
            type="button"
            onClick={addBlackout}
            className="text-xs tracking-[0.15em] uppercase border border-forest/30 px-3 py-1 hover:bg-forest hover:text-cream transition-colors"
          >
            Add
          </button>
        </div>
        {value.blackout_dates.length === 0 ? (
          <div className="text-xs text-forest/50 italic">No blackouts</div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {value.blackout_dates.map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => removeBlackout(d)}
                className="text-xs px-2 py-1 bg-forest/10 text-forest hover:bg-terracotta hover:text-cream transition-colors"
                title="Click to remove"
              >
                {d} ×
              </button>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="text-xs tracking-[0.2em] uppercase text-forest/60 mb-2">Notes</div>
        <textarea
          rows={2}
          value={value.notes}
          onChange={(e) => onChange({ ...value, notes: e.target.value })}
          placeholder="Anything else we should know about scheduling?"
          className="w-full border border-forest/20 px-3 py-2 text-sm bg-cream focus:outline-none focus:border-terracotta"
        />
      </div>
    </div>
  );
}

// ─── Resident preferences card ─────────────────────────────────────────
function ResidentPrefsCard({
  resident,
  onSaved,
}: {
  resident: ResidentRow;
  onSaved: () => void;
}) {
  const [yard, setYard] = useState<YardSize | ''>(resident.yard_size || '');
  const [avail, setAvail] = useState<Availability>(parseAvail(resident.availability_json));
  const [contactPref, setContactPref] = useState(resident.contact_pref || 'phone');
  const [accessNotes, setAccessNotes] = useState(resident.access_notes || '');
  const [gateCode, setGateCode] = useState(resident.gate_code || '');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const res = await fetch('/api/me/profile', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          resident: {
            id: resident.id,
            yard_size: yard || null,
            availability_json: avail,
            contact_pref: contactPref,
            access_notes: accessNotes,
            gate_code: gateCode,
          },
        }),
      });
      const j = await res.json() as { ok: boolean; error?: string };
      if (!j.ok) throw new Error(j.error || 'Save failed');
      setMsg('Saved.');
      onSaved();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="bg-cream border border-forest/15 p-6 mb-6">
      <h3 className="text-xl text-forest font-display mb-1">Your service preferences</h3>
      <p className="text-sm text-forest/60 mb-5">
        Application #{resident.id} · {resident.address}, {resident.city}
      </p>

      <div className="space-y-6">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-forest/60 mb-2">Yard size</div>
          <div className="grid sm:grid-cols-2 gap-2">
            {(['small','medium','large','xl'] as YardSize[]).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setYard(s)}
                className={`text-left px-4 py-2 border text-sm transition-colors ${
                  yard === s
                    ? 'border-terracotta bg-terracotta/5 text-forest'
                    : 'border-forest/20 hover:border-terracotta text-forest/80'
                }`}
              >
                {YARD_LABEL[s]}
              </button>
            ))}
          </div>
        </div>

        <AvailabilityEditor value={avail} onChange={setAvail} />

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-forest/60 mb-2">Contact preference</div>
            <select
              value={contactPref}
              onChange={(e) => setContactPref(e.target.value)}
              className="w-full border border-forest/20 px-3 py-2 text-sm bg-cream focus:outline-none focus:border-terracotta"
            >
              <option value="phone">Phone call</option>
              <option value="text">Text message</option>
              <option value="email">Email</option>
            </select>
          </div>
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-forest/60 mb-2">Gate code (optional)</div>
            <input
              value={gateCode}
              onChange={(e) => setGateCode(e.target.value)}
              maxLength={60}
              placeholder="e.g. 1234#"
              className="w-full border border-forest/20 px-3 py-2 text-sm bg-cream focus:outline-none focus:border-terracotta"
            />
          </div>
        </div>

        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-forest/60 mb-2">Access notes</div>
          <textarea
            rows={3}
            value={accessNotes}
            onChange={(e) => setAccessNotes(e.target.value)}
            placeholder="Dogs in the backyard? Side gate locked? Park on which side?"
            className="w-full border border-forest/20 px-3 py-2 text-sm bg-cream focus:outline-none focus:border-terracotta"
          />
        </div>
      </div>

      <div className="mt-5 flex items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="text-xs tracking-[0.15em] uppercase bg-forest text-cream px-5 py-2 hover:bg-terracotta transition-colors disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save preferences'}
        </button>
        {msg && <span className="text-sm text-forest/70">{msg}</span>}
      </div>
    </section>
  );
}

// ─── Mowkin preferences card (volunteer OR provider) ───────────────────
function MowkinPrefsCard({
  kind,
  row,
  onSaved,
}: {
  kind: 'volunteer' | 'provider';
  row: VolunteerRow | ProviderRow;
  onSaved: () => void;
}) {
  const [maxYard, setMaxYard] = useState<YardSize | ''>(row.max_yard_size || '');
  const [avail, setAvail] = useState<Availability>(parseAvail(row.availability_json));
  const [radius, setRadius] = useState<number>(row.service_radius_miles ?? 15);
  const [canHaul, setCanHaul] = useState<string>(
    kind === 'volunteer' ? ((row as VolunteerRow).can_haul || 'no') : 'yes',
  );
  const [county, setCounty] = useState(row.preferred_county || '');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const label = kind === 'volunteer'
    ? `Volunteer #${row.id} — ${(row as VolunteerRow).first_name} ${(row as VolunteerRow).last_name}`
    : `${(row as ProviderRow).business_name}`;

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const body = kind === 'volunteer'
        ? {
            volunteer: {
              id: row.id,
              max_yard_size: maxYard || null,
              availability_json: avail,
              service_radius_miles: radius,
              can_haul: canHaul,
              preferred_county: county,
            },
          }
        : {
            provider: {
              id: row.id,
              max_yard_size: maxYard || null,
              availability_json: avail,
              service_radius_miles: radius,
              preferred_county: county,
            },
          };
      const res = await fetch('/api/me/profile', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const j = await res.json() as { ok: boolean; error?: string };
      if (!j.ok) throw new Error(j.error || 'Save failed');
      setMsg('Saved.');
      onSaved();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="bg-cream border border-forest/15 p-6 mb-6">
      <h3 className="text-xl text-forest font-display mb-1">
        Your {kind === 'volunteer' ? 'volunteer' : 'pro'} availability
      </h3>
      <p className="text-sm text-forest/60 mb-5">{label}</p>

      <div className="space-y-6">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-forest/60 mb-2">Max yard size I'll take</div>
          <div className="grid sm:grid-cols-2 gap-2">
            {(['small','medium','large','xl'] as YardSize[]).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setMaxYard(s)}
                className={`text-left px-4 py-2 border text-sm transition-colors ${
                  maxYard === s
                    ? 'border-terracotta bg-terracotta/5 text-forest'
                    : 'border-forest/20 hover:border-terracotta text-forest/80'
                }`}
              >
                {YARD_LABEL[s]}
              </button>
            ))}
          </div>
        </div>

        <AvailabilityEditor value={avail} onChange={setAvail} />

        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-forest/60 mb-2">Service radius</div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={0}
                max={200}
                value={radius}
                onChange={(e) => setRadius(Number(e.target.value))}
                className="w-20 border border-forest/20 px-3 py-2 text-sm bg-cream focus:outline-none focus:border-terracotta"
              />
              <span className="text-sm text-forest/60">miles</span>
            </div>
          </div>
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-forest/60 mb-2">Preferred county</div>
            <select
              value={county}
              onChange={(e) => setCounty(e.target.value)}
              className="w-full border border-forest/20 px-3 py-2 text-sm bg-cream focus:outline-none focus:border-terracotta"
            >
              <option value="">No preference</option>
              <option value="Nueces">Nueces</option>
              <option value="Aransas">Aransas</option>
              <option value="San Patricio">San Patricio</option>
            </select>
          </div>
          {kind === 'volunteer' && (
            <div>
              <div className="text-xs tracking-[0.2em] uppercase text-forest/60 mb-2">Can haul gear</div>
              <select
                value={canHaul}
                onChange={(e) => setCanHaul(e.target.value)}
                className="w-full border border-forest/20 px-3 py-2 text-sm bg-cream focus:outline-none focus:border-terracotta"
              >
                <option value="yes">Yes (truck/trailer)</option>
                <option value="no">No</option>
              </select>
            </div>
          )}
        </div>
      </div>

      <div className="mt-5 flex items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="text-xs tracking-[0.15em] uppercase bg-forest text-cream px-5 py-2 hover:bg-terracotta transition-colors disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save availability'}
        </button>
        {msg && <span className="text-sm text-forest/70">{msg}</span>}
      </div>
    </section>
  );
}

// ─── Assignment card ───────────────────────────────────────────────────
function AssignmentCard({
  a,
  perspective,
  onChanged,
}: {
  a: AssignmentRow;
  perspective: 'resident' | 'mowkin';
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [showNote, setShowNote] = useState(false);

  const act = async (action: 'accept' | 'decline' | 'complete' | 'note') => {
    setBusy(true);
    try {
      const res = await fetch(`/api/me/assignment/${a.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, notes: note || undefined }),
      });
      const j = await res.json() as { ok: boolean; error?: string };
      if (!j.ok) throw new Error(j.error || 'Action failed');
      setNote(''); setShowNote(false);
      onChanged();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const isUpcoming = new Date(a.scheduled_date + 'T23:59:59') >= new Date();
  const canAccept   = perspective === 'mowkin' && a.status === 'proposed';
  const canComplete = perspective === 'mowkin' && a.status === 'accepted';

  return (
    <div className={`border p-4 ${a.status === 'cancelled' ? 'border-forest/10 bg-cream/40 opacity-70' : 'border-forest/15 bg-cream'}`}>
      <div className="flex items-center gap-3 flex-wrap mb-2">
        <StatusPill status={a.status} />
        <span className="text-sm text-forest font-medium">{a.scheduled_date}</span>
        {a.time_window && (
          <span className="text-xs uppercase tracking-[0.15em] text-forest/60">{a.time_window}</span>
        )}
        {isUpcoming && a.status !== 'cancelled' && a.status !== 'completed' && (
          <span className="text-[10px] tracking-[0.15em] uppercase bg-terracotta text-cream px-2 py-0.5">Upcoming</span>
        )}
      </div>

      {perspective === 'mowkin' ? (
        <div className="text-forest text-sm">
          <div className="font-medium">{a.resident_name}</div>
          <div className="text-forest/70">{a.resident_address}, {a.resident_city}</div>
          {a.resident_yard_size && (
            <div className="text-xs uppercase tracking-[0.15em] text-forest/60 mt-1">
              Yard: {a.resident_yard_size}
            </div>
          )}
        </div>
      ) : (
        <div className="text-forest text-sm">
          <div className="font-medium">Mowkin: {a.mowkin_label || 'unassigned'}</div>
          {a.mowkin_kind && (
            <div className="text-xs uppercase tracking-[0.15em] text-forest/60">{a.mowkin_kind}</div>
          )}
        </div>
      )}

      {a.admin_notes && (
        <div className="text-xs text-forest/70 mt-2 italic">Admin: {a.admin_notes}</div>
      )}
      {a.mowkin_notes && (
        <div className="text-xs text-forest/70 mt-1 italic">Mowkin: {a.mowkin_notes}</div>
      )}

      {(canAccept || canComplete) && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {canAccept && (
            <>
              <button
                type="button"
                disabled={busy}
                onClick={() => act('accept')}
                className="text-xs tracking-[0.15em] uppercase bg-forest text-cream px-3 py-1.5 hover:bg-terracotta transition-colors disabled:opacity-50"
              >
                Accept
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => setShowNote((s) => !s)}
                className="text-xs tracking-[0.15em] uppercase border border-terracotta text-terracotta px-3 py-1.5 hover:bg-terracotta hover:text-cream transition-colors disabled:opacity-50"
              >
                Decline...
              </button>
            </>
          )}
          {canComplete && (
            <button
              type="button"
              disabled={busy}
              onClick={() => setShowNote((s) => !s)}
              className="text-xs tracking-[0.15em] uppercase bg-forest text-cream px-3 py-1.5 hover:bg-terracotta transition-colors disabled:opacity-50"
            >
              Mark complete...
            </button>
          )}
        </div>
      )}

      {showNote && (
        <div className="mt-3">
          <textarea
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={canComplete ? 'Anything to flag about the mow?' : 'Reason for declining (optional)'}
            className="w-full border border-forest/20 px-3 py-2 text-sm bg-cream focus:outline-none focus:border-terracotta"
          />
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => act(canComplete ? 'complete' : 'decline')}
              className="text-xs tracking-[0.15em] uppercase bg-forest text-cream px-3 py-1.5 hover:bg-terracotta transition-colors disabled:opacity-50"
            >
              {canComplete ? 'Confirm complete' : 'Confirm decline'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────
export function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState<ApiData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      const res = await fetch('/api/me/dashboard', { credentials: 'include' });
      const json = await res.json() as { ok: true; data: ApiData } | { ok: false; error: string };
      if (json.ok) setData(json.data); else setError(json.error);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Network error');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(); }, []);

  const { upcoming, past, residentAssignments, mowkinAssignments } = useMemo(() => {
    if (!data) return { upcoming: [], past: [], residentAssignments: [], mowkinAssignments: [] };
    const residentIds = new Set(data.residents.map((r) => r.id));
    const volunteerIds = new Set(data.volunteers.map((v) => v.id));
    const providerIds  = new Set(data.providers.map((p) => p.id));
    const residentAssignments = data.assignments.filter((a) => residentIds.has(a.resident_id));
    const mowkinAssignments = data.assignments.filter(
      (a) =>
        (a.volunteer_id && volunteerIds.has(a.volunteer_id)) ||
        (a.provider_id  && providerIds.has(a.provider_id)),
    );
    const today = new Date().toISOString().slice(0, 10);
    const upcoming = data.assignments.filter((a) => a.scheduled_date >= today && a.status !== 'cancelled');
    const past = data.assignments.filter((a) => a.scheduled_date < today || a.status === 'completed');
    return { upcoming, past, residentAssignments, mowkinAssignments };
  }, [data]);

  if (loading) {
    return <div className="max-w-5xl mx-auto px-6 py-16 text-forest/60 italic">Loading...</div>;
  }
  if (error || !data) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-16">
        <div className="bg-terracotta/10 border-l-4 border-terracotta p-4 text-terracotta text-sm">
          {error || 'Could not load dashboard.'}
        </div>
      </div>
    );
  }

  const anyApps =
    data.residents.length + data.providers.length + data.volunteers.length + data.hires.length > 0;
  const approvedResidents = data.residents.filter((r) => r.status === 'approved' && !r.archived_at);
  const vettedVolunteers = data.volunteers.filter((v) => v.status === 'vetted' && !v.archived_at);
  const approvedProviders = data.providers.filter((p) => p.status === 'approved' && !p.archived_at);

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-12 py-16">
      <div className="text-xs tracking-[0.25em] uppercase text-terracotta mb-3">Your Dashboard</div>
      <h1
        className="text-5xl text-forest mb-3 leading-tight font-display"
        style={{ fontVariationSettings: '"opsz" 144' }}
      >
        Welcome back{user?.name ? `, ${user.name}` : ''}.
      </h1>
      <p className="text-forest/70 mb-12">
        Signed in as <span className="text-forest">{user?.email}</span>.
        {user?.role === 'admin' && (
          <>
            {' '}
            <Link to="/admin" className="text-terracotta hover:underline">Go to admin →</Link>
          </>
        )}
      </p>

      <CycleEndBanner />
      <BidInvitations />

      {/* ── Upcoming hero card ── */}
      {upcoming.length > 0 && (
        <section className="mb-10">
          <h2 className="text-2xl text-forest font-display mb-4">Upcoming mows</h2>
          <div className="grid md:grid-cols-2 gap-3">
            {upcoming.slice(0, 4).map((a) => {
              const perspective =
                a.volunteer_id || a.provider_id
                  ? mowkinAssignments.some((x) => x.id === a.id) ? 'mowkin' : 'resident'
                  : 'resident';
              return (
                <AssignmentCard key={a.id} a={a} perspective={perspective} onChanged={load} />
              );
            })}
          </div>
        </section>
      )}

      {/* ── Empty state ── */}
      {!anyApps && (
        <div className="bg-cream border border-forest/15 p-8 mb-8">
          <h2 className="text-2xl text-forest mb-3 font-display">No applications yet.</h2>
          <p className="text-forest/75 mb-6 leading-relaxed">
            You haven't submitted anything to MowKind yet. Four ways to participate:
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <Link to="/apply" className="block border border-forest/15 hover:border-terracotta p-4 transition-colors">
              <div className="text-xs tracking-[0.2em] uppercase text-terracotta mb-1">Resident</div>
              <div className="font-display text-lg text-forest">Request a free mow</div>
            </Link>
            <Link to="/provider" className="block border border-forest/15 hover:border-terracotta p-4 transition-colors">
              <div className="text-xs tracking-[0.2em] uppercase text-terracotta mb-1">Pro</div>
              <div className="font-display text-lg text-forest">Become a Mowkin</div>
            </Link>
            <Link to="/volunteer" className="block border border-forest/15 hover:border-terracotta p-4 transition-colors">
              <div className="text-xs tracking-[0.2em] uppercase text-terracotta mb-1">Volunteer</div>
              <div className="font-display text-lg text-forest">Become a Volunteer Mowkin</div>
            </Link>
            <Link to="/hire" className="block border border-forest/15 hover:border-terracotta p-4 transition-colors">
              <div className="text-xs tracking-[0.2em] uppercase text-terracotta mb-1">Customer</div>
              <div className="font-display text-lg text-forest">Hire a Mowkin</div>
            </Link>
          </div>
        </div>
      )}

      {/* ── Resident: preferences + application list + assignments ── */}
      {approvedResidents.map((r) => (
        <ResidentPrefsCard key={`prefs-${r.id}`} resident={r} onSaved={load} />
      ))}

      {data.residents.length > 0 && (
        <section className="mb-10">
          <h2 className="text-2xl text-forest font-display mb-4">Your resident applications</h2>
          <div className="space-y-3">
            {data.residents.map((r) => (
              <div key={r.id} className={`border p-4 ${r.archived_at ? 'border-forest/10 bg-cream/50 opacity-60' : 'border-forest/15 bg-cream'}`}>
                <div className="flex items-center gap-3 flex-wrap mb-1">
                  <StatusPill status={r.status} />
                  {r.archived_at && (
                    <span className="text-[10px] tracking-[0.15em] uppercase bg-forest/10 text-forest/60 px-2 py-0.5">Archived</span>
                  )}
                  <span className="text-sm text-forest/60">Submitted {fmtDate(r.created_at)}</span>
                </div>
                <div className="text-forest">{r.address}, {r.city}, {r.zip}</div>
                {r.cycle_end && r.status === 'approved' && (
                  <div className="text-sm text-forest/70 mt-2">
                    Service window: {fmtDate(r.cycle_start)} → {fmtDate(r.cycle_end)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {residentAssignments.length > 0 && (
        <section className="mb-10">
          <h2 className="text-2xl text-forest font-display mb-4">Your service history</h2>
          <div className="space-y-3">
            {residentAssignments.map((a) => (
              <AssignmentCard key={`r-${a.id}`} a={a} perspective="resident" onChanged={load} />
            ))}
          </div>
        </section>
      )}

      {/* ── Mowkin: preferences + assignment queue ── */}
      {vettedVolunteers.map((v) => (
        <MowkinPrefsCard key={`vol-${v.id}`} kind="volunteer" row={v} onSaved={load} />
      ))}
      {approvedProviders.map((p) => (
        <MowkinPrefsCard key={`prov-${p.id}`} kind="provider" row={p} onSaved={load} />
      ))}

      {mowkinAssignments.length > 0 && (
        <section className="mb-10">
          <h2 className="text-2xl text-forest font-display mb-4">Your assigned mows</h2>
          <div className="space-y-3">
            {mowkinAssignments.map((a) => (
              <AssignmentCard key={`m-${a.id}`} a={a} perspective="mowkin" onChanged={load} />
            ))}
          </div>
        </section>
      )}

      {/* ── Applications without prefs (pending/denied) ── */}
      {data.volunteers.length > 0 && (
        <section className="mb-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl text-forest font-display">Your volunteer applications</h2>
            <Link to="/my-mow-days" className="text-sm tracking-[0.1em] uppercase text-terracotta hover:text-forest">My Mow Days →</Link>
          </div>
          <div className="space-y-3">
            {data.volunteers.map((v) => (
              <div key={v.id} className={`border p-4 ${v.archived_at ? 'border-forest/10 bg-cream/50 opacity-60' : 'border-forest/15 bg-cream'}`}>
                <div className="flex items-center gap-3 flex-wrap mb-1">
                  <StatusPill status={v.status} />
                  {v.archived_at && <span className="text-[10px] tracking-[0.15em] uppercase bg-forest/10 text-forest/60 px-2 py-0.5">Archived</span>}
                  <span className="text-sm text-forest/60">Applied {fmtDate(v.created_at)}</span>
                </div>
                <div className="text-forest">{v.first_name} {v.last_name} · {v.city || '—'} ({v.county || '—'})</div>
                {v.vetted_at && <div className="text-sm text-forest/65 mt-1">Vetted {fmtDate(v.vetted_at)}</div>}
              </div>
            ))}
          </div>
        </section>
      )}

      {data.providers.length > 0 && (
        <section className="mb-10">
          <h2 className="text-2xl text-forest font-display mb-4">Your Pro Mowkin applications</h2>
          <div className="space-y-3">
            {data.providers.map((p) => (
              <div key={p.id} className={`border p-4 ${p.archived_at ? 'border-forest/10 bg-cream/50 opacity-60' : 'border-forest/15 bg-cream'}`}>
                <div className="flex items-center gap-3 flex-wrap mb-1">
                  <StatusPill status={p.status} />
                  {p.archived_at && <span className="text-[10px] tracking-[0.15em] uppercase bg-forest/10 text-forest/60 px-2 py-0.5">Archived</span>}
                  <span className="text-sm text-forest/60">Applied {fmtDate(p.created_at)}</span>
                </div>
                <div className="text-forest font-medium">{p.business_name}</div>
                <div className="text-sm text-forest/65">{p.capacity_per_month} mows/month · {p.owner_name}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {data.hires.length > 0 && (
        <section className="mb-10">
          <h2 className="text-2xl text-forest font-display mb-4">Your paid service requests</h2>
          <div className="space-y-3">
            {data.hires.map((h) => (
              <div key={h.id} className={`border p-4 ${h.archived_at ? 'border-forest/10 bg-cream/50 opacity-60' : 'border-forest/15 bg-cream'}`}>
                <div className="flex items-center gap-3 flex-wrap mb-1">
                  <StatusPill status={h.status} />
                  <span className="text-sm text-forest/60">{fmtDate(h.created_at)}</span>
                </div>
                <div className="text-forest">{h.contact_name} · {h.service_type}</div>
                <div className="text-sm text-forest/65">{h.service_address}, {h.city}, {h.zip}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {past.length > 0 && (
        <section className="mb-10">
          <h2 className="text-2xl text-forest font-display mb-4">Past mows</h2>
          <div className="space-y-3">
            {past.slice(0, 25).map((a) => {
              const persp = mowkinAssignments.some((x) => x.id === a.id) ? 'mowkin' : 'resident';
              return <AssignmentCard key={`p-${a.id}`} a={a} perspective={persp} onChanged={load} />;
            })}
          </div>
        </section>
      )}
    </div>
  );
}
